import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { docService } from '../services/api';
import { HiExclamationCircle, HiDocumentText } from 'react-icons/hi';

import { HiArrowLeft, HiDownload, HiRefresh, HiClipboardCopy, HiCheck } from 'react-icons/hi';
import { useDocumentTypes } from '../context/DocumentTypesContext';
import { DOC_STATUS, STATUS, prettyLabel } from '../theme/tokens';

const ONLYOFFICE_URL = process.env.REACT_APP_DOCUMENT_OFFICE_URL;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';

const OFFICE_DOC_TYPES = {
  doc: 'word', docx: 'word',
  // Spreadsheets are synced from S3 (HR report folders), so they need a viewer
  // too — OnlyOffice renders them as the 'cell' document type.
  xls: 'cell', xlsx: 'cell',
  // ppt: 'slide', pptx: 'slide',
};

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']);
const TIF_EXTS = new Set(['tif', 'tiff']);

const getFileExt = (filename) =>
  filename?.split('.').pop()?.toLowerCase() || '';

// Load the OnlyOffice script once; resolves when DocsAPI is ready.
const loadOnlyOfficeScript = (src) =>
  new Promise((resolve, reject) => {
    if (window.DocsAPI) { resolve(); return; }
    let script = document.querySelector(`script[src="${src}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = src;
      document.head.appendChild(script);
    }
    if (window.DocsAPI) { resolve(); return; }
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load OnlyOffice script')), { once: true });
  });

// ---------------------------------------------------------------------------
// OnlyOffice viewer — fetches JWT-signed config from backend, view-only
// ---------------------------------------------------------------------------
const OnlyOfficeViewer = ({ docId }) => {
  const [error, setError] = useState(null);
  const editorRef = useRef(null);
  const destroyedRef = useRef(false);

  useEffect(() => {
    destroyedRef.current = false;

    const init = async () => {
      if (!ONLYOFFICE_URL) {
        setError('REACT_APP_DOCUMENT_OFFICE_URL is not configured.');
        return;
      }

      let editorConfig;
      try {
        const { config, token } = await docService.getEditorConfig(docId);
        editorConfig = token ? { ...config, token } : config;
      } catch {
        setError('Could not load document configuration.');
        return;
      }

      try {
        await loadOnlyOfficeScript(`${ONLYOFFICE_URL}/web-apps/apps/api/documents/api.js`);
      } catch {
        setError('Could not reach OnlyOffice server.');
        return;
      }

      if (destroyedRef.current) return;

      if (editorRef.current) {
        try { editorRef.current.destroyEditor(); } catch {}
      }

      editorRef.current = new window.DocsAPI.DocEditor('onlyoffice-container', editorConfig);
    };

    init();

    return () => {
      destroyedRef.current = true;
      if (editorRef.current) {
        try { editorRef.current.destroyEditor(); } catch {}
        editorRef.current = null;
      }
    };
  }, [docId]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 text-slate-400 h-full">
        <HiExclamationCircle className="w-8 h-8" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return <div id="onlyoffice-container" className="w-full h-full" />;
};

// ---------------------------------------------------------------------------
// TIF viewer — loads converted PNG from backend
// ---------------------------------------------------------------------------
const TifViewer = ({ docId, filename }) => {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/documents/${docId}/preview`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Preview failed');
        return res.blob();
      })
      .then((blob) => setSrc(URL.createObjectURL(blob)))
      .catch(() => setError(true));
  }, [docId]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 text-slate-400 h-full">
        <HiExclamationCircle className="w-8 h-8" />
        <p className="text-sm">Could not render TIF preview</p>
      </div>
    );
  }
  if (!src) {
    return <div className="flex items-center justify-center h-full text-slate-400 text-sm">Converting image…</div>;
  }
  return (
    <img
      src={src}
      alt={filename}
      className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
    />
  );
};

// ---------------------------------------------------------------------------
// Document viewer — routes to the right viewer by file extension
// ---------------------------------------------------------------------------
const DocumentViewer = ({ doc, viewUrl, downloadUrl }) => {
  const ext = getFileExt(doc.filename);

  // The row outlived its file. Say so plainly — previously the viewer was handed
  // a presigned URL for a missing key and the browser rendered S3's raw
  // <Error>NoSuchKey</Error> XML inside the preview pane.
  if (doc.storage_missing) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 text-center px-8 h-full">
        <HiExclamationCircle className="w-10 h-10" style={{ color: STATUS.serious }} aria-hidden="true" />
        <p className="text-slate-700 font-medium">This file is no longer in storage</p>
        <p className="text-sm text-slate-500 max-w-md">
          The document record still exists, but its file has been removed from the S3
          bucket. It is normally cleared from the list within a couple of sync cycles.
        </p>
        <p className="text-xs text-slate-400 font-mono break-all max-w-lg mt-1">{doc.s3_key}</p>
      </div>
    );
  }

  if (ext in OFFICE_DOC_TYPES) {
    return <OnlyOfficeViewer docId={String(doc.id)} />;
  }

  if (ext === 'pdf' && viewUrl) {
    return (
      <iframe
        src={`${viewUrl}#toolbar=0`}
        className="w-full h-full rounded-lg shadow-lg bg-white"
        title="PDF Viewer"
      />
    );
  }

  if (TIF_EXTS.has(ext)) {
    return <TifViewer docId={String(doc.id)} filename={doc.filename} />;
  }

  if (IMAGE_EXTS.has(ext)) {
    return <TifViewer docId={String(doc.id)} filename={doc.filename} />;
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 text-slate-400 h-full">
      <HiDocumentText className="w-10 h-10" />
      <p>Preview not available for .{ext || 'unknown'} files</p>
      {downloadUrl && (
        <a href={downloadUrl} className="btn-secondary" download>
          Download to view
        </a>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const DocumentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Type labels come from the catalog, so an admin-added type shows its real
  // name here instead of the raw key.
  const { labelOf } = useDocumentTypes();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [expandedOcr, setExpandedOcr] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const fetchDoc = useCallback(() => {
    setLoading(true);
    docService.getOne(id)
      .then(setDocument)
      .catch((err) => console.error('Failed to fetch document', err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await docService.retry(id);
      fetchDoc(); // reload to show updated status
    } catch {
      alert('Failed to queue retry');
    } finally {
      setRetrying(false);
    }
  };

  const handleCopyOcr = () => {
    if (document?.ocr_text) {
      navigator.clipboard.writeText(document.ocr_text);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }
  };

  if (loading) return <div className="page-container">Loading...</div>;
  if (!document) return <div className="page-container">Document not found</div>;

  const viewUrl = document.s3_url;
  const downloadUrl = document.download_url;
  const isFailed = document.status === 'failed';

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: Metadata Sidebar */}
      <div className="w-96 bg-white border-r border-slate-200 overflow-y-auto p-6 flex flex-col h-full">

        <div className="mb-6">
          {/* The page is reachable by direct URL and from a new tab, so it needs
              its own way back — the previous build had this commented out. */}
          <button
            onClick={() => (location.key !== 'default' ? navigate(-1) : navigate('/documents'))}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
          >
            <HiArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
          </button>

          <h1 className="text-lg font-semibold text-slate-800 break-words mb-3" title={document.filename}>
            {document.filename}
          </h1>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
              {labelOf(document.document_type)}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: (DOC_STATUS[document.status] ?? {}).color ?? '#898781' }}
                aria-hidden="true"
              />
              {(DOC_STATUS[document.status] ?? {}).label ?? document.status}
            </span>
          </div>

          {/* Error alert + Retry button */}
          {isFailed && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs font-semibold text-red-700 mb-1">Processing failed</p>
              {document.processing_error && (
                <p className="text-xs text-red-600 break-words font-mono leading-relaxed">
                  {document.processing_error}
                </p>
              )}
              <button
                onClick={handleRetry}
                disabled={retrying}
                className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors"
              >
                <HiRefresh className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} aria-hidden="true" />
                {retrying ? 'Queuing retry…' : 'Retry processing'}
              </button>
            </div>
          )}

          {downloadUrl && !document.storage_missing && (
            <a
              href={downloadUrl}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              <HiDownload className="w-4 h-4" aria-hidden="true" /> Download
            </a>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Extracted data</h4>
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-100">
              <div>
                <label className="text-xs text-slate-500 block">Document Number</label>
                <p className="font-medium text-slate-800">{document.document_number || 'N/A'}</p>
              </div>
              {/* <div>
                <label className="text-xs text-slate-500 block">Vendor</label>
                <p className="font-medium text-slate-800">{document.vendor_id || 'N/A'}</p>
              </div> */}
              <div>
                <label className="text-xs text-slate-500 block">Date</label>
                <p className="font-medium text-slate-800">{document.document_date || 'N/A'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-500 block">Amount</label>
                <p className="font-medium text-slate-800">
                  {document.amount
                    ? new Intl.NumberFormat('vi-VN', { style: 'currency', currency: document.currency || 'VND' }).format(document.amount)
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">OCR text</h4>
              {document.ocr_text && (
                <div className="flex gap-2">
                  <button
                    onClick={handleCopyOcr}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                    title="Copy OCR text"
                  >
                    {copiedToClipboard
                      ? <><HiCheck className="w-3 h-3" aria-hidden="true" /> Copied</>
                      : <><HiClipboardCopy className="w-3 h-3" aria-hidden="true" /> Copy</>}
                  </button>
                  <button
                    onClick={() => setExpandedOcr(!expandedOcr)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    {expandedOcr ? 'Show less' : 'Show more'}
                  </button>
                </div>
              )}
            </div>
            <div className={`bg-slate-50 rounded-lg p-4 border border-slate-100 ${expandedOcr ? 'max-h-96' : 'max-h-32'} overflow-y-auto transition-all`}>
              {document.ocr_text ? (
                <>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap break-words font-mono">
                    {expandedOcr ? document.ocr_text : document.ocr_text.substring(0, 300)}
                    {!expandedOcr && document.ocr_text.length > 300 && (
                      <span className="text-slate-500">...</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-200">
                    {document.ocr_text.length.toLocaleString()} characters • {document.ocr_text.trim().split(/\s+/).filter(Boolean).length.toLocaleString()} words
                  </p>
                </>
              ) : (
                <p className="text-xs text-slate-500">No OCR text available</p>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Details</h4>
            <dl className="text-sm space-y-2">
              {[
                ['Site', document.site_name],
                ['Department', document.department],
                ['Source', document.source_type ? prettyLabel(document.source_type) : null],
                ['Uploaded', document.created_at ? new Date(document.created_at).toLocaleString() : null],
                ['Size', document.file_size ? `${(document.file_size / 1024 / 1024).toFixed(2)} MB` : null],
                ['Language', document.detected_language],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="text-slate-500 shrink-0">{label}</dt>
                  <dd className={`text-right truncate ${value ? 'text-slate-700' : 'text-slate-300'}`}>
                    {value || '—'}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Right: Document Viewer */}
      <div className="flex-1 bg-slate-100 flex items-center justify-center overflow-hidden">
        <DocumentViewer doc={document} viewUrl={viewUrl} downloadUrl={downloadUrl} />
      </div>
    </div>
  );
};

export default DocumentDetailPage;
